// 事件/剧情系统 - 对话、分支、动画、音效

export interface DialogueChoice {
  id: string;
  text: string;
  condition?: {
    flag?: string;
    attribute?: { name: string; min: number };
    item?: string;
    relationship?: { npc: string; min: number };
  };
  nextNode?: string;
  action?: DialogueAction;
}

export interface DialogueAction {
  type: 'setFlag' | 'giveItem' | 'removeItem' | 'modifyAttribute' | 
        'modifyRelationship' | 'startQuest' | 'completeQuest' | 
        'changeScene' | 'playSound' | 'showEffect' | 'wait' | 'script';
  params: Record<string, any>;
}

export interface DialogueNode {
  id: string;
  speaker?: string;
  speakerName?: string;
  emotion?: string;
  text?: string;
  textVariants?: {
    condition: string;
    text: string;
  }[];
  choices?: DialogueChoice[];
  nextNode?: string;
  actions?: DialogueAction[];
  autoAdvance?: boolean;
  autoAdvanceDelay?: number;
  soundEffect?: string;
}

export interface StoryScene {
  id: string;
  background?: string;
  music?: string;
  soundEffect?: string;
  effect?: string;
  dialogues: DialogueNode[];
  onStart?: DialogueAction[];
  onEnd?: DialogueAction[];
}

export interface StoryChapter {
  id: string;
  title: string;
  description: string;
  scenes: string[];
  prerequisites?: {
    flags?: string[];
    quests?: string[];
    age?: number;
  };
}

// 故事数据
export const STORY_DATA: Record<string, StoryScene> = {
  // 开场
  'intro': {
    id: 'intro',
    background: 'bg/sky.jpg',
    music: 'bgm/peaceful.mp3',
    onStart: [
      { type: 'showEffect', params: { effect: 'fadeIn', duration: 2000 } }
    ],
    dialogues: [
      {
        id: 'intro_01',
        speaker: 'narrator',
        text: '2016年春，放牛山脚下的小村庄。',
        autoAdvance: true,
        autoAdvanceDelay: 2000,
        nextNode: 'intro_02',
      },
      {
        id: 'intro_02',
        speaker: 'narrator',
        text: '六岁的欢欢，迎来了生命中最重要的伙伴...',
        autoAdvance: true,
        autoAdvanceDelay: 2000,
        nextNode: 'intro_03',
      },
      {
        id: 'intro_03',
        speaker: 'system',
        speakerName: '系统',
        text: '欢迎来到《放牛山》2.0！请开始你的故事。',
        choices: [
          {
            id: 'start_game',
            text: '开始游戏',
            action: { type: 'setFlag', params: { flag: 'game_started', value: true } },
            nextNode: 'ch1_start',
          },
          {
            id: 'load_game',
            text: '加载存档',
            action: { type: 'script', params: { script: 'openLoadMenu' } },
          }
        ]
      }
    ]
  },
  
  // 第一章开始
  'ch1_start': {
    id: 'ch1_start',
    background: 'bg/village_morning.jpg',
    music: 'bgm/morning.mp3',
    soundEffect: 'sfx/birds.mp3',
    onStart: [
      { type: 'setFlag', params: { flag: 'chapter1_started', value: true } },
      { type: 'changeScene', params: { scene: 'village_home' } }
    ],
    dialogues: [
      {
        id: 'ch1_01',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'smile',
        text: '欢欢，起床啦！太阳都晒屁股了。',
        nextNode: 'ch1_02',
      },
      {
        id: 'ch1_02',
        speaker: 'player',
        speakerName: '欢欢',
        emotion: 'sleepy',
        text: '奶奶...我还想再睡一会儿...',
        nextNode: 'ch1_03',
      },
      {
        id: 'ch1_03',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'gentle',
        text: '快起来吧，今天村里有集市，奶奶带你去转转。',
        choices: [
          {
            id: 'agree',
            text: '好的，我这就起来！',
            action: { type: 'modifyAttribute', params: { attr: 'happiness', amount: 5 } },
            nextNode: 'ch1_04',
          },
          {
            id: 'refuse',
            text: '我不想去...',
            action: { type: 'modifyAttribute', params: { attr: 'happiness', amount: -5 } },
            nextNode: 'ch1_04_alt',
          }
        ]
      },
      {
        id: 'ch1_04',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'happy',
        text: '好孩子，快洗漱吃饭吧。',
        actions: [
          { type: 'startQuest', params: { questId: 'main_ch1_01' } }
        ],
        nextNode: 'ch1_end',
      },
      {
        id: 'ch1_04_alt',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'concerned',
        text: '怎么啦？是不是哪里不舒服？',
        choices: [
          {
            id: 'explain',
            text: '没有，只是有点困...',
            nextNode: 'ch1_04',
          },
          {
            id: 'silent',
            text: '...',
            nextNode: 'ch1_05',
          }
        ]
      },
      {
        id: 'ch1_05',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'worried',
        text: '那好吧，你再休息一会儿。奶奶给你熬点粥。',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'constitution', amount: 5 } }
        ],
        nextNode: 'ch1_end',
      },
      {
        id: 'ch1_end',
        speaker: 'narrator',
        text: '就这样，新的一天开始了...',
        actions: [
          { type: 'showEffect', params: { effect: 'fadeOut', duration: 1000 } }
        ]
      }
    ]
  },
  
  // 初遇丹丹
  'meet_dandan': {
    id: 'meet_dandan',
    background: 'bg/footpath.jpg',
    music: 'bgm/encounter.mp3',
    soundEffect: 'sfx/dog_whine.mp3',
    dialogues: [
      {
        id: 'meet_01',
        speaker: 'narrator',
        text: '走在山路上，你听到草丛里传来窸窸窣窣的声音。',
        autoAdvance: true,
        autoAdvanceDelay: 1500,
        nextNode: 'meet_02',
      },
      {
        id: 'meet_02',
        speaker: 'narrator',
        text: '拨开草丛，一只小黄狗正瑟瑟发抖地缩在那里。',
        autoAdvance: true,
        autoAdvanceDelay: 1500,
        nextNode: 'meet_03',
      },
      {
        id: 'meet_03',
        speaker: 'dandan',
        speakerName: '???',
        emotion: 'scared',
        text: '呜...呜...',
        soundEffect: 'sfx/puppy_cry.mp3',
        choices: [
          {
            id: 'approach',
            text: '慢慢靠近',
            nextNode: 'meet_04',
          },
          {
            id: 'call',
            text: '轻声呼唤',
            nextNode: 'meet_04_alt',
          },
          {
            id: 'leave',
            text: '离开',
            action: { type: 'setFlag', params: { flag: 'ignored_dandan', value: true } },
            nextNode: 'meet_leave',
          }
        ]
      },
      {
        id: 'meet_04',
        speaker: 'narrator',
        text: '你慢慢地靠近小狗，它起初有些害怕，但很快便放松下来。',
        nextNode: 'meet_05',
      },
      {
        id: 'meet_04_alt',
        speaker: 'narrator',
        text: '你轻声呼唤着，小狗抬起头，用湿漉漉的眼睛看着你。',
        nextNode: 'meet_05',
      },
      {
        id: 'meet_05',
        speaker: 'player',
        speakerName: '欢欢',
        emotion: 'surprised',
        text: '好可爱的小狗！你怎么一个人在这里？',
        nextNode: 'meet_06',
      },
      {
        id: 'meet_06',
        speaker: 'dandan',
        speakerName: '???',
        emotion: 'hopeful',
        text: '汪...',
        nextNode: 'meet_07',
      },
      {
        id: 'meet_07',
        speaker: 'narrator',
        text: '小狗蹭了蹭你的手，看起来饿坏了。',
        choices: [
          {
            id: 'feed',
            text: '给它食物',
            condition: { item: 'food' },
            action: { type: 'removeItem', params: { itemId: 'food', quantity: 1 } },
            nextNode: 'meet_feed',
          },
          {
            id: 'pet',
            text: '抚摸它',
            nextNode: 'meet_pet',
          },
          {
            id: 'take_home',
            text: '带它回家',
            nextNode: 'meet_take_home',
          }
        ]
      },
      {
        id: 'meet_feed',
        speaker: 'dandan',
        speakerName: '???',
        emotion: 'happy',
        text: '汪！汪！',
        soundEffect: 'sfx/dog_eat.mp3',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 20 } },
          { type: 'setFlag', params: { flag: 'fed_dandan', value: true } }
        ],
        nextNode: 'meet_take_home',
      },
      {
        id: 'meet_pet',
        speaker: 'dandan',
        speakerName: '???',
        emotion: 'happy',
        text: '（摇尾巴）',
        soundEffect: 'sfx/dog_happy.mp3',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 10 } }
        ],
        choices: [
          {
            id: 'take_home2',
            text: '带它回家',
            nextNode: 'meet_take_home',
          }
        ]
      },
      {
        id: 'meet_take_home',
        speaker: 'player',
        speakerName: '欢欢',
        emotion: 'happy',
        text: '跟我回家吧，以后我就是你的家人了！',
        nextNode: 'meet_naming',
      },
      {
        id: 'meet_naming',
        speaker: 'narrator',
        text: '你想给这只小狗取什么名字？',
        choices: [
          {
            id: 'name_dandan',
            text: '丹丹',
            action: { type: 'setFlag', params: { flag: 'named_dandan', value: true } },
            nextNode: 'meet_end',
          },
          {
            id: 'name_other',
            text: '小黄',
            action: { type: 'setFlag', params: { flag: 'named_xiaohuang', value: true } },
            nextNode: 'meet_end_alt',
          }
        ]
      },
      {
        id: 'meet_end',
        speaker: 'player',
        speakerName: '欢欢',
        emotion: 'happy',
        text: '以后你就叫丹丹了！丹丹，我们回家！',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'happiness', amount: 30 } },
          { type: 'modifyRelationship', params: { npc: 'dandan', amount: 20 } },
          { type: 'completeQuest', params: { questId: 'main_ch1_01' } },
          { type: 'startQuest', params: { questId: 'main_ch1_02' } },
          { type: 'setFlag', params: { flag: 'has_dandan', value: true } }
        ],
        nextNode: 'meet_final',
      },
      {
        id: 'meet_end_alt',
        speaker: 'player',
        speakerName: '欢欢',
        emotion: 'happy',
        text: '以后你就叫小黄了！小黄，我们回家！',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'happiness', amount: 30 } },
          { type: 'setFlag', params: { flag: 'has_dandan', value: true } }
        ],
        nextNode: 'meet_final',
      },
      {
        id: 'meet_final',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'happy',
        text: '汪汪！',
        soundEffect: 'sfx/dog_bark_happy.mp3',
        actions: [
          { type: 'showEffect', params: { effect: 'heart', count: 3 } }
        ]
      },
      {
        id: 'meet_leave',
        speaker: 'narrator',
        text: '你转身离开了，身后传来小狗微弱的呜咽声...',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'happiness', amount: -10 } }
        ]
      }
    ]
  },
  
  // 奶奶的日常对话
  'grandma_daily': {
    id: 'grandma_daily',
    background: 'bg/home_interior.jpg',
    dialogues: [
      {
        id: 'grandma_greeting_morning',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'smile',
        textVariants: [
          { condition: 'weather_sunny', text: '今天天气真好，适合出去走走。' },
          { condition: 'weather_rainy', text: '下雨了，记得带把伞。' },
          { condition: 'default', text: '欢欢，饿了吗？奶奶给你做好吃的。' }
        ],
        choices: [
          {
            id: 'chat',
            text: '和奶奶聊天',
            nextNode: 'grandma_chat',
          },
          {
            id: 'cook',
            text: '帮奶奶做饭',
            nextNode: 'grandma_cook',
          },
          {
            id: 'leave',
            text: '我出去玩一会儿',
            nextNode: 'grandma_bye',
          }
        ]
      },
      {
        id: 'grandma_chat',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'happy',
        text: '我们欢欢又长大了，越来越懂事了。',
        actions: [
          { type: 'modifyRelationship', params: { npc: 'grandma', amount: 5 } }
        ]
      },
      {
        id: 'grandma_cook',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'happy',
        text: '好孩子，来，奶奶教你做你最爱吃的菜。',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'wisdom', amount: 5 } },
          { type: 'modifyRelationship', params: { npc: 'grandma', amount: 10 } }
        ]
      },
      {
        id: 'grandma_bye',
        speaker: 'grandma',
        speakerName: '奶奶',
        emotion: 'gentle',
        text: '去吧，别跑太远，记得按时回来吃饭。'
      }
    ]
  },
  
  // 丹丹的日常互动
  'dandan_interact': {
    id: 'dandan_interact',
    background: 'bg/yard.jpg',
    soundEffect: 'sfx/dog_wag_tail.mp3',
    dialogues: [
      {
        id: 'dandan_greeting',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'happy',
        textVariants: [
          { condition: 'intimacy_low', text: '（小心翼翼地看着你）' },
          { condition: 'intimacy_medium', text: '汪！（摇尾巴）' },
          { condition: 'intimacy_high', text: '汪汪汪！（兴奋地扑向你）' }
        ],
        choices: [
          {
            id: 'pet',
            text: '抚摸',
            nextNode: 'dandan_pet',
          },
          {
            id: 'feed',
            text: '喂食',
            nextNode: 'dandan_feed',
          },
          {
            id: 'play',
            text: '玩耍',
            nextNode: 'dandan_play',
          },
          {
            id: 'talk',
            text: '说话',
            condition: { flag: 'intimacy_high' },
            nextNode: 'dandan_talk',
          }
        ]
      },
      {
        id: 'dandan_pet',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'happy',
        text: '（舒服地眯起眼睛）',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 5 } },
          { type: 'modifyAttribute', params: { attr: 'happiness', amount: 5 } }
        ]
      },
      {
        id: 'dandan_feed',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'excited',
        text: '（开心地吃着）汪汪！',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 10 } },
          { type: 'modifyAttribute', params: { attr: 'dandan.health', amount: 5 } }
        ]
      },
      {
        id: 'dandan_play',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'excited',
        text: '（叼来球，期待地看着你）',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 8 } },
          { type: 'modifyAttribute', params: { attr: 'constitution', amount: 3 } }
        ]
      },
      {
        id: 'dandan_talk',
        speaker: 'dandan',
        speakerName: '丹丹',
        emotion: 'loving',
        text: '（虽然不会说话，但眼神中满是依恋）',
        actions: [
          { type: 'modifyAttribute', params: { attr: 'dandan.intimacy', amount: 15 } },
          { type: 'modifyAttribute', params: { attr: 'happiness', amount: 10 } }
        ]
      }
    ]
  }
};

// 故事章节数据
export const STORY_CHAPTERS: StoryChapter[] = [
  {
    id: 'chapter1',
    title: '童年早期',
    description: '6-8岁的时光，初遇丹丹，开始上学',
    scenes: ['ch1_start', 'meet_dandan'],
  },
  {
    id: 'chapter2',
    title: '童年中期',
    description: '8-10岁的成长，丹丹生病，好朋友离别',
    scenes: [],
    prerequisites: {
      flags: ['chapter1_complete'],
      age: 8,
    }
  },
  {
    id: 'chapter3',
    title: '少年早期',
    description: '10-12岁的探索，发现写作天赋',
    scenes: [],
    prerequisites: {
      flags: ['chapter2_complete'],
      age: 10,
    }
  },
  {
    id: 'chapter4',
    title: '少年中期',
    description: '12-14岁的迷茫，青春期叛逆',
    scenes: [],
    prerequisites: {
      flags: ['chapter3_complete'],
      age: 12,
    }
  },
  {
    id: 'chapter5',
    title: '青春期',
    description: '14-16岁的成长，坚定梦想',
    scenes: [],
    prerequisites: {
      flags: ['chapter4_complete'],
      age: 14,
    }
  }
];

// 事件系统管理器
export class EventSystem {
  private scenes: Map<string, StoryScene> = new Map();
  private currentScene: StoryScene | null = null;
  private currentNode: DialogueNode | null = null;
  private history: string[] = [];
  
  private onTextDisplay?: (text: string, speaker?: string, emotion?: string) => void;
  private onChoicesDisplay?: (choices: DialogueChoice[]) => void;
  private onAction?: (action: DialogueAction) => void;
  private onSceneChange?: (sceneId: string) => void;
  
  constructor() {
    // 加载故事数据
    Object.entries(STORY_DATA).forEach(([id, scene]) => {
      this.scenes.set(id, scene);
    });
  }
  
  // 设置回调
  setCallbacks(callbacks: {
    onTextDisplay?: (text: string, speaker?: string, emotion?: string) => void;
    onChoicesDisplay?: (choices: DialogueChoice[]) => void;
    onAction?: (action: DialogueAction) => void;
    onSceneChange?: (sceneId: string) => void;
  }): void {
    this.onTextDisplay = callbacks.onTextDisplay;
    this.onChoicesDisplay = callbacks.onChoicesDisplay;
    this.onAction = callbacks.onAction;
    this.onSceneChange = callbacks.onSceneChange;
  }
  
  // 开始场景
  async startScene(sceneId: string): Promise<void> {
    const scene = this.scenes.get(sceneId);
    if (!scene) {
      console.error(`Scene not found: ${sceneId}`);
      return;
    }
    
    this.currentScene = scene;
    this.history = [];
    
    if (this.onSceneChange) {
      this.onSceneChange(sceneId);
    }
    
    // 执行场景开始动作
    if (scene.onStart) {
      for (const action of scene.onStart) {
        await this.executeAction(action);
      }
    }
    
    // 开始第一个对话
    if (scene.dialogues.length > 0) {
      await this.showNode(scene.dialogues[0].id);
    }
  }
  
  // 显示对话节点
  async showNode(nodeId: string): Promise<void> {
    if (!this.currentScene) return;
    
    const node = this.currentScene.dialogues.find(n => n.id === nodeId);
    if (!node) {
      console.error(`Dialogue node not found: ${nodeId}`);
      return;
    }
    
    this.currentNode = node;
    this.history.push(nodeId);
    
    // 获取文本
    let text = node.text || '';
    if (node.textVariants) {
      // TODO: 根据条件选择变体
      text = node.textVariants[0]?.text || text;
    }
    
    // 显示文本
    if (this.onTextDisplay) {
      this.onTextDisplay(text, node.speakerName, node.emotion);
    }
    
    // 执行节点动作
    if (node.actions) {
      for (const action of node.actions) {
        await this.executeAction(action);
      }
    }
    
    // 自动前进
    if (node.autoAdvance && node.nextNode) {
      setTimeout(() => {
        this.showNode(node.nextNode!);
      }, node.autoAdvanceDelay || 1000);
    } else if (node.choices) {
      // 显示选项
      if (this.onChoicesDisplay) {
        this.onChoicesDisplay(node.choices);
      }
    } else if (node.nextNode) {
      // 继续下一个节点
      await this.showNode(node.nextNode);
    } else {
      // 场景结束
      await this.endScene();
    }
  }
  
  // 选择选项
  async selectChoice(choiceId: string): Promise<void> {
    if (!this.currentNode || !this.currentNode.choices) return;
    
    const choice = this.currentNode.choices.find(c => c.id === choiceId);
    if (!choice) return;
    
    // 执行选项动作
    if (choice.action) {
      await this.executeAction(choice.action);
    }
    
    // 继续下一个节点
    if (choice.nextNode) {
      await this.showNode(choice.nextNode);
    }
  }
  
  // 执行动作
  private async executeAction(action: DialogueAction): Promise<void> {
    if (this.onAction) {
      this.onAction(action);
    }
    
    // 内置动作处理
    switch (action.type) {
      case 'wait':
        await new Promise(resolve => setTimeout(resolve, action.params.duration || 1000));
        break;
      case 'script':
        // 自定义脚本处理
        console.log('Executing script:', action.params.script);
        break;
    }
  }
  
  // 结束场景
  async endScene(): Promise<void> {
    if (!this.currentScene) return;
    
    // 执行场景结束动作
    if (this.currentScene.onEnd) {
      for (const action of this.currentScene.onEnd) {
        await this.executeAction(action);
      }
    }
    
    this.currentScene = null;
    this.currentNode = null;
  }
  
  // 获取当前场景
  getCurrentScene(): StoryScene | null {
    return this.currentScene;
  }
  
  // 获取场景列表
  getSceneList(): string[] {
    return Array.from(this.scenes.keys());
  }
}